import { useSelectedAlert } from "@/context/SelectedAlertContext";
import { useAppSelector } from "@/redux/dispatch";
import { RootState } from "@/redux/store/rootReducers";
import {
  AudioInputControl,
  AudioOutputControl,
  ControlBar,
  LocalVideo,
  MeetingStatus,
  Phone,
  RemoteVideos,
  VideoInputControl,
  useLocalVideo,
  useMeetingManager,
  useMeetingStatus,
  useRemoteVideoTileState,
} from "amazon-chime-sdk-component-library-react";
import { User } from "lucide-react";
import { useEffect } from "react";

interface MeetingModalProps {
  onClose: () => void;
}

const MeetingModal = ({ onClose }: MeetingModalProps) => {
  const meetingManager = useMeetingManager();
  const meetingStatus = useMeetingStatus();
  const { isMeetingStart } = useAppSelector((state: RootState) => state.meeting)
  const { selectedAlertId } = useSelectedAlert();
  const { isVideoEnabled } = useLocalVideo();

  const { tileId: selfTileId } = useLocalVideo();
  const remoteTiles: any = useRemoteVideoTileState(); // Fetch remote participants' tiles

  const handleEndMeeting = async () => {
    const meetingId = meetingManager.meetingId;
    if (meetingId) {
      await meetingManager.leave();
    }
    onClose();
  };
  useEffect(() => {
    if (!isMeetingStart || !selectedAlertId) {
      handleEndMeeting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAlertId, isMeetingStart])

  return (
    <div className="relative w-full h-screen bg-white shadow-xl  flex flex-col">
      <div className="flex flex-col h-full bg-dark p-3">
        {/* Self Video Tile (Always on Top) */}
        <div className="flex-1 max-h-[60%] bg-dark m-1 rounded-lg overflow-hidden ">
          {selfTileId && isVideoEnabled ? (
            <LocalVideo
              nameplate="Me"
              className="w-full h-full"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500 bg-dark">
              <div className='h-50 bg-black p-3 rounded-full' >
                <User height={50} width={50} color='white' />
              </div>
            </div>
          )}
        </div>

        {/* Remote Video Tiles (Below Self Video) */}
        <div className="flex-1 min-h-0 max-h-[40%] flex flex-row justify-center pb-1">
          {meetingStatus === MeetingStatus.Succeeded && remoteTiles?.tiles.length > 0 ? (
            <RemoteVideos className="m-1 rounded-lg overflow-hidden" />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-200">
              No one is sharing video at this moment...
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center w-full p-2 rounded-sm bg-transparent">
        <ControlBar
          layout="undocked-horizontal"
          showLabels={false}
          className="flex justify-center items-center gap-4 w-full shadow-none"
        >
          <AudioInputControl />
          <VideoInputControl />
          <AudioOutputControl />
          <button
            onClick={handleEndMeeting}
            className="flex items-center justify-center p-2 rounded-full bg-red-500 hover:bg-red-700 transition-colors"
            aria-label="End Meeting"
          >
            <Phone className="h-7 w-7 text-white" />
          </button>
        </ControlBar>
      </div>
    </div>
  );
};

export default MeetingModal;
